export * from "ce/reducers/uiReducers/workspaceReducer";
import type { ReduxAction } from "actions/ReduxActionTypes";
import {
  ReduxActionErrorTypes,
  ReduxActionTypes,
} from "ee/constants/ReduxActionConstants";
import {
  handlers as CE_handlers,
  initialState as CE_initialState,
} from "ce/reducers/uiReducers/workspaceReducer";
import type { WorkspaceReduxState as CE_WorkspaceReduxState } from "ce/reducers/uiReducers/workspaceReducer";
import { createImmerReducer } from "utils/ReducerUtils";
import type { Package } from "ee/constants/PackageConstants";
import type { PackageMetadata } from "ee/constants/PackageConstants";
import type {
  CreatePackageFromWorkspacePayload,
  DeletePackagePayload,
} from "ee/actions/packageActions";
import type {
  Workflow,
  WorkflowMetadata,
} from "ee/constants/WorkflowConstants";
import type { DeleteWorkflowPayload } from "ee/actions/workflowActions";

export const initialState: WorkspaceReduxState = {
  ...CE_initialState,
  groupSuggestions: [],

  loadingStates: {
    ...CE_initialState.loadingStates,
    isFetchingPackagesList: false,
    isFetchingWorkflowsList: false,
    packageCreationRequestMap: {},
    workflowCreationRequestMap: {},
    isImportingPackage: false,
    isImportingWorkflow: false,
  },
  packagesList: [],
  workflowsList: [],
  importedPackage: null,
  importedWorkflow: null,
  isSavingPkgName: false,
  isSavingWorkflowName: false,
  isErrorSavingPkgName: false,
  isErrorSavingWorkflowName: false,
  createPackageModal: {
    isOpen: false,
    workspaceId: "",
  },
};

type ID = string;

type LoadingStates = CE_WorkspaceReduxState["loadingStates"] & {
  isFetchingPackagesList: boolean;
  isImportingPackage: boolean;
  isImportingWorkflow: boolean;
  isFetchingWorkflowsList: boolean;
  packageCreationRequestMap: Record<ID, boolean>;
  workflowCreationRequestMap: Record<ID, boolean>;
};

type FilteredCE_WorkspaceRedux = Omit<CE_WorkspaceReduxState, "loadingStates">;

export interface GroupSuggestions {
  id: string;
  name: string;
}

export interface CreatePackageModalState {
  isOpen: boolean;
  workspaceId: string;
}

export interface WorkspaceReduxState extends FilteredCE_WorkspaceRedux {
  groupSuggestions: GroupSuggestions[];
  loadingStates: LoadingStates;
  packagesList: PackageMetadata[];
  workflowsList: WorkflowMetadata[];
  isSavingPkgName: boolean;
  isSavingWorkflowName: boolean;
  isErrorSavingPkgName: boolean;
  isErrorSavingWorkflowName: boolean;
  importedPackage: Package | null;
  importedWorkflow: Workflow | null;
  createPackageModal: CreatePackageModalState;
}

const handlers = {
  ...CE_handlers,

  [ReduxActionTypes.OPEN_CREATE_PACKAGE_MODAL]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<{ workspaceId: string }>,
  ) => {
    draftState.createPackageModal = {
      isOpen: true,
      workspaceId: action.payload.workspaceId,
    };

    return draftState;
  },

  [ReduxActionTypes.CLOSE_CREATE_PACKAGE_MODAL]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.createPackageModal.isOpen = false;
    draftState.createPackageModal.workspaceId = "";

    return draftState;
  },

  [ReduxActionTypes.FETCH_GROUP_SUGGESTIONS_SUCCESS]: (
    draftState: WorkspaceReduxState,
    // TODO: Fix this the next time the file is edited
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: ReduxAction<any>,
  ) => {
    draftState.groupSuggestions = action.payload;
  },

  [ReduxActionErrorTypes.FETCH_GROUP_SUGGESTIONS_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.groupSuggestions = [];
  },

  [ReduxActionTypes.FETCH_PACKAGES_FOR_WORKSPACE_INIT]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isFetchingPackagesList = true;

    return draftState;
  },

  [ReduxActionErrorTypes.FETCH_PACKAGES_FOR_WORKSPACE_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isFetchingPackagesList = false;

    return draftState;
  },

  [ReduxActionTypes.FETCH_PACKAGES_FOR_WORKSPACE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<PackageMetadata[]>,
  ) => {
    draftState.loadingStates.isFetchingPackagesList = false;

    draftState.packagesList = action.payload || [];

    return draftState;
  },

  [ReduxActionTypes.CREATE_PACKAGE_FROM_WORKSPACE_INIT]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<CreatePackageFromWorkspacePayload>,
  ) => {
    const { workspaceId } = action.payload;

    draftState.loadingStates.packageCreationRequestMap[workspaceId] = true;

    return draftState;
  },

  [ReduxActionTypes.CREATE_PACKAGE_FROM_WORKSPACE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<Package>,
  ) => {
    const { workspaceId } = action.payload;

    draftState.loadingStates.packageCreationRequestMap[workspaceId] = false;

    return draftState;
  },

  [ReduxActionErrorTypes.CREATE_PACKAGE_FROM_WORKSPACE_ERROR]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<{ workspaceId: string }>,
  ) => {
    const { workspaceId } = action.payload;

    draftState.loadingStates.packageCreationRequestMap[workspaceId] = false;

    return draftState;
  },

  [ReduxActionTypes.DELETE_PACKAGE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<DeletePackagePayload>,
  ) => {
    const { id } = action.payload;
    const index = draftState.packagesList.findIndex((p) => p.id === id);

    if (index !== -1) draftState.packagesList.splice(index, 1);

    return draftState;
  },

  [ReduxActionTypes.UPDATE_PACKAGE_INIT]: (draftState: WorkspaceReduxState) => {
    return {
      ...draftState,
      isSavingPkgName: true,
      isErrorSavingPkgName: false,
    };
  },

  [ReduxActionTypes.UPDATE_PACKAGE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<Package>,
  ) => {
    const pkg = action.payload;
    const index = draftState.packagesList.findIndex((p) => p.id === pkg.id);

    if (index !== -1) draftState.packagesList[index] = pkg;

    draftState.isSavingPkgName = false;
    draftState.isErrorSavingPkgName = false;

    return draftState;
  },

  [ReduxActionErrorTypes.UPDATE_PACKAGE_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    return {
      ...draftState,
      isSavingPkgName: false,
      isErrorSavingPkgName: true,
    };
  },

  [ReduxActionTypes.CREATE_WORKFLOW_FROM_WORKSPACE_INIT]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<CreatePackageFromWorkspacePayload>,
  ) => {
    const { workspaceId } = action.payload;

    draftState.loadingStates.workflowCreationRequestMap[workspaceId] = true;

    return draftState;
  },

  [ReduxActionTypes.CREATE_WORKFLOW_FROM_WORKSPACE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<Package>,
  ) => {
    const { workspaceId } = action.payload;

    draftState.loadingStates.workflowCreationRequestMap[workspaceId] = false;

    return draftState;
  },

  [ReduxActionErrorTypes.CREATE_WORKFLOW_FROM_WORKSPACE_ERROR]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<{ workspaceId: string }>,
  ) => {
    const { workspaceId } = action.payload;

    draftState.loadingStates.workflowCreationRequestMap[workspaceId] = false;

    return draftState;
  },

  [ReduxActionTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_INIT]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isFetchingWorkflowsList = true;

    return draftState;
  },

  [ReduxActionErrorTypes.FETCH_ALL_WORKFLOWS_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isFetchingWorkflowsList = false;

    return draftState;
  },

  [ReduxActionErrorTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isFetchingWorkflowsList = false;

    return draftState;
  },

  [ReduxActionTypes.FETCH_ALL_WORKFLOWS_FOR_WORKSPACE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<WorkflowMetadata[]>,
  ) => {
    draftState.loadingStates.isFetchingWorkflowsList = false;

    draftState.workflowsList = action.payload || [];

    return draftState;
  },

  [ReduxActionTypes.DELETE_WORKFLOW_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<DeleteWorkflowPayload>,
  ) => {
    const { id } = action.payload;
    const index = draftState.workflowsList.findIndex((p) => p.id === id);

    if (index !== -1) draftState.workflowsList.splice(index, 1);

    return draftState;
  },

  [ReduxActionTypes.UPDATE_WORKFLOW_NAME_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<Workflow>,
  ) => {
    const workflow = action.payload;
    const index = draftState.workflowsList.findIndex(
      (w) => w.id === workflow.id,
    );

    if (index !== -1) {
      draftState.workflowsList[index] = {
        ...draftState.workflowsList[index],
        name: workflow.name,
        modifiedAt: workflow.modifiedAt,
        modifiedBy: workflow.modifiedBy,
      };
    }

    draftState.isSavingWorkflowName = false;
    draftState.isErrorSavingWorkflowName = false;

    return draftState;
  },

  [ReduxActionErrorTypes.UPDATE_WORKFLOW_NAME_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    return {
      ...draftState,
      isSavingWorkflowName: false,
      isErrorSavingWorkflowName: true,
    };
  },

  [ReduxActionTypes.IMPORT_PACKAGE_INIT]: (draftState: WorkspaceReduxState) => {
    draftState.loadingStates.isImportingPackage = true;
  },

  [ReduxActionTypes.IMPORT_PACKAGE_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<Package>,
  ) => {
    draftState.loadingStates.isImportingPackage = false;
    draftState.importedPackage = action.payload;
    draftState.packagesList.push(action.payload);
  },

  [ReduxActionTypes.RESET_IMPORT_DATA]: (draftState: WorkspaceReduxState) => {
    draftState.importedPackage = null;
  },

  [ReduxActionErrorTypes.IMPORT_PACKAGE_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isImportingPackage = false;
  },

  [ReduxActionTypes.IMPORT_WORKFLOW_INIT]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isImportingWorkflow = true;
  },

  [ReduxActionTypes.IMPORT_WORKFLOW_SUCCESS]: (
    draftState: WorkspaceReduxState,
    action: ReduxAction<Workflow>,
  ) => {
    draftState.loadingStates.isImportingWorkflow = false;

    const workflow = action.payload;

    if (workflow && workflow.id) {
      // Check if workflow already exists in the list
      const existingIndex = draftState.workflowsList.findIndex(
        (w) => w.id === workflow.id,
      );

      if (existingIndex !== -1) {
        draftState.workflowsList[existingIndex] = {
          ...draftState.workflowsList[existingIndex],
          ...workflow,
        };
      } else {
        draftState.workflowsList.push(workflow);
      }

      // Set the imported workflow for the reconnect modal
      draftState.importedWorkflow = workflow;
    }

    return draftState;
  },

  [ReduxActionErrorTypes.IMPORT_WORKFLOW_ERROR]: (
    draftState: WorkspaceReduxState,
  ) => {
    draftState.loadingStates.isImportingWorkflow = false;
  },
};

const workspaceReducer = createImmerReducer(initialState, handlers);

export default workspaceReducer;
